import { Link } from "react-router";
import { AlertCircle, ArrowRight, Clock, Users } from "lucide-react";

import { StatusDot } from "@/components/atomic-crm/shared/StatusDot";
import { TypeBadge } from "@/components/atomic-crm/shared/TypeBadge";

import type { DevCardPayload } from "./types";

// Sprint 9.3 — Bloc DevCard pour Dev D2.
// Émis par tool list_dev_tasks_blocking_release ou search_dev_tasks.

const PRIO_BAR = {
  critique: "bg-fabrik-red w-full",
  haute: "bg-fabrik-orange w-3/4",
  moyenne: "bg-fabrik-blue w-1/2",
  basse: "bg-fabrik-text-3 w-1/4",
} as const;

const ENV_LABEL = {
  prod: "PROD",
  dev: "DEV",
  mobile: "MOBILE",
  api: "API",
} as const;

export function DevCardBlock({ payload }: { payload: DevCardPayload }) {
  return (
    <Link
      to={`/dev_tasks/${payload.id}`}
      className="block rounded-fabrik-card border border-border bg-card hover:bg-fabrik-green-soft/30 transition-colors px-4 py-3 shadow-card"
    >
      <div className="flex items-start gap-3">
        {payload.priority && (
          <div className="w-1 self-stretch rounded-full bg-fabrik-gray-pill overflow-hidden shrink-0 min-h-12">
            <div className={`block h-full ${PRIO_BAR[payload.priority]}`} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {payload.ref && (
              <span className="font-mono text-[11px] text-fabrik-text-3">
                {payload.ref}
              </span>
            )}
            {payload.type && <TypeBadge type={payload.type} />}
            {payload.env && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-fabrik-text-3 px-1.5 py-0.5 rounded bg-fabrik-gray-pill">
                {ENV_LABEL[payload.env]}
              </span>
            )}
            {payload.blocking_release && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-fabrik-red font-semibold">
                <AlertCircle className="size-3" />
                Bloquant release
              </span>
            )}
          </div>
          <p className="mt-1 text-[14px] font-semibold text-foreground truncate">
            {payload.title}
          </p>
          <div className="mt-1 flex items-center gap-3 text-[12px] text-fabrik-text-3">
            {payload.status && (
              <span className="inline-flex items-center gap-1.5">
                <StatusDot status={payload.status} size={8} />
                {payload.status}
              </span>
            )}
            {payload.deadline && (
              <span className="inline-flex items-center gap-1">
                <Clock className="size-3" />
                {payload.deadline}
              </span>
            )}
            {typeof payload.clients_count === "number" && (
              <span className="inline-flex items-center gap-1">
                <Users className="size-3" />
                {payload.clients_count} client
                {payload.clients_count > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        <ArrowRight className="size-4 text-fabrik-text-3 shrink-0 mt-1" />
      </div>
    </Link>
  );
}
